import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, MapPin, Package, Receipt, ShieldCheck, Truck, Clock, CalendarClock, Sun, Sunset, AlertTriangle, PackagePlus, CheckCircle2, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatKm, SIZE_LABEL, DELIVERY_TYPE_LABEL, DELIVERY_TYPE_DESC, expressServiceDescription } from "@/lib/format";
import { DELIVERY_TYPE_ICON } from "@/lib/delivery_icons";
import StructuredAddressForm, { isStructuredAddressValid } from "@/components/StructuredAddressForm";
import PhonePrefixInput from "@/components/PhonePrefixInput";
import SharedSustainabilityBadge from "@/components/SharedSustainabilityBadge";
import CrossBorderNotice from "@/components/CrossBorderNotice";
import SubscriptionBreakdown from "@/components/SubscriptionBreakdown";
import OrderTrackingMap from "@/components/OrderTrackingMap";
import ParcelLabelCard from "@/components/ParcelLabelCard";
import { useAuth } from "@/lib/auth";
import { splitPhone } from "@/lib/phone_split";
import { SERVICE_HOURS, swissServiceTime, fromSwissLocalInput } from "@/lib/serviceTime";
import { orderPaymentBlock } from "@/lib/orderPaymentStatus";
import { normalizeParcelSelection, selectDeliveryService, selectParcelCategory } from "@/lib/parcelSelection";
import CheckoutVerification from "@/components/CheckoutVerification";
import { PARCEL_CATEGORIES } from "@/lib/parcelCategories";
import { informationUrl, SUPPORT_PHONE, SUPPORT_PHONE_RAW } from "@/lib/contact";
import { formatVatRate, isBusinessCustomer, validPriceTax } from "@/lib/priceTax";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";

const SIZES = PARCEL_CATEGORIES;

const DELIVERY_OPTIONS = [
  { id: "shared",   label: "Shared",   icon: DELIVERY_TYPE_ICON.shared,   base: "CHF 9 + 0.55/km",  min: "min CHF 15", desc: DELIVERY_TYPE_DESC.shared },
  { id: "standard", label: "Standard", icon: DELIVERY_TYPE_ICON.standard, base: "CHF 12 + 0.85/km", min: "min CHF 24", desc: DELIVERY_TYPE_DESC.standard },
  { id: "express",  label: "Express",  icon: DELIVERY_TYPE_ICON.express,  base: "CHF 19 + 1.40/km", min: "min CHF 39", desc: DELIVERY_TYPE_DESC.express },
  { id: "van",      label: "Van",      icon: DELIVERY_TYPE_ICON.van,      base: "CHF 35 + 2.20/km", min: "min CHF 69", desc: DELIVERY_TYPE_DESC.van },
];

const TIME_SLOT_OPTIONS = [
  { id: "day", label: "Giorno", time: "08:00 – 18:00", surcharge: "Nessuna maggiorazione", icon: Sun, badge: null },
  { id: "evening", label: "Sera", time: "18:00 – 21:00", surcharge: "+20%", icon: Sunset, badge: "Su disponibilità driver" },
  { id: "night", label: "Mattino presto", time: "06:30 – 08:00", surcharge: "+50%", icon: Sun, badge: "Su disponibilità driver" },
];

const PHONE_RE = /^[+0-9 ()\-./]{4,30}$/;

export default function NewOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const business = isBusinessCustomer(user?.role);
  const [step, setStep] = useState(1);

  // Phase 43.30 — Guard ruolo: variabile booleana, NIENTE early return prima degli hook.
  // (Il ProtectedRoute in App.js già blocca a livello router, questo è solo un
  // fallback diagnostico per il caso di sovrapposizione sessioni.)
  const CUSTOMER_ROLES_FE = ["customer_private", "customer_business", "customer_shop"];
  const wrongRole = !!user && !CUSTOMER_ROLES_FE.includes(user.role);

  // Phase 43.11 — scroll-to-top all'apertura e ad ogni cambio step.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); } catch { /* ignore */ }
  }, [step]);
  const [form, setForm] = useState({
    pickup_address: "",
    pickup_lat: null,
    pickup_lng: null,
    pickup_struct: { street: "", house_number: "", zip: "", city: "", canton: "TI", country: "Svizzera" },
    pickup_contact_name: "",
    pickup_contact_phone: "",
    pickup_phone_prefix: "+41",
    delivery_address: "",
    delivery_lat: null,
    delivery_lng: null,
    delivery_struct: { street: "", house_number: "", zip: "", city: "", canton: "TI", country: "Svizzera" },
    delivery_contact_name: "",
    delivery_contact_phone: "",
    delivery_phone_prefix: "+41",
    // Sender (auto-prefilled from auth user — editable per-order)
    sender_name: "",
    sender_phone: "",
    sender_phone_prefix: "+41",
    sender_company: "",
    package_description: "",
    package_size: "medium",
    delivery_type: "standard",
    notes: "",
    // when
    when_mode: "now", // "now" | "scheduled"
    requested_pickup_at: "", // ISO local datetime string from <input type=datetime-local>
    time_slot: "day",
  });
  const [quote, setQuote] = useState(null);
  // Express/Van remain request-only even if an API omits or contradicts metadata.
  const reviewService = form.package_size === "xl" ? "van" : form.delivery_type;
  const reviewLabel = DELIVERY_TYPE_LABEL[reviewService];
  const requiresManualReview = ["van", "express"].includes(reviewService) || quote?.requires_manual_review === true;
  // The tariff remains in the breakdown; funding determines the cash still due.
  const amountDue = quote?.funding_preview?.amount_due_chf ?? quote?.final_price;
  const quoteTax = validPriceTax(quote?.price_tax) ? quote.price_tax : null;
  const chargeTax = validPriceTax(quote?.charge_tax) ? quote.charge_tax : null;
  const quoteTaxReady = !business || (quoteTax?.audience === "business" && chargeTax?.audience === "business");
  const netCreditKnown = quoteTax?.audience === "business" && chargeTax?.audience === "business"
    && quoteTax.vat_rate === chargeTax.vat_rate && quoteTax.net_chf >= chargeTax.net_chf;
  const inclusionCredit = netCreditKnown
    ? (Math.round(quoteTax.net_chf * 100) - Math.round(chargeTax.net_chf * 100)) / 100
    : quote?.subscription_credit_applied;
  const parcel = SIZES.find(item => item.id === form.package_size);
  const reviewDescription = reviewService === "express"
    ? expressServiceDescription(quote?.delivery_sla_minutes)
    : "Il team DapNow deve verificare il collo, gli accessi e la movimentazione prima di prenotare o pagare.";
  const parcelLimits = reviewService === "van" ? "25 kg e 120 × 80 × 80 cm" : parcel?.specs;
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);  // Phase 44.04 — order doc post-create (with payment_source)
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  useEffect(() => {
    const refreshTime = () => setCurrentTime(Date.now());
    const timer = window.setInterval(refreshTime, 30000);
    window.addEventListener("focus", refreshTime);
    document.addEventListener("visibilitychange", refreshTime);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshTime);
      document.removeEventListener("visibilitychange", refreshTime);
    };
  }, []);
  // Payment actions follow the server release configuration; missing config fails closed.
  const [paymentConfig, setPaymentConfig] = useState(null);
  useEffect(() => {
    api.get("/public/config")
      .then(({ data }) => setPaymentConfig(data))
      .catch(() => setPaymentConfig(null));
  }, []);

  // Prefill from public homepage calculator (saved in localStorage)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("dapnow_prefill_quote");
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || !data.pickup_address) return;
      // expire after 1h
      const savedAt = data.saved_at ? new Date(data.saved_at).getTime() : 0;
      if (Date.now() - savedAt > 60 * 60 * 1000) {
        localStorage.removeItem("dapnow_prefill_quote");
        return;
      }
      setForm((s) => ({
        ...s,
        pickup_address: data.pickup_address || s.pickup_address,
        delivery_address: data.delivery_address || s.delivery_address,
        pickup_struct: data.pickup_struct || s.pickup_struct,
        delivery_struct: data.delivery_struct || s.delivery_struct,
        ...normalizeParcelSelection(data.delivery_type || s.delivery_type, data.package_size || s.package_size),
        time_slot: data.time_slot || s.time_slot,
      }));
      localStorage.removeItem("dapnow_prefill_quote");
    } catch { /* ignore */ }
  }, []);

  // Phase 32 Batch A — prefill sender (and pickup-contact) from authenticated user
  useEffect(() => {
    if (!user) return;
    const userSender = splitPhone(user.phone || "", user.phone_prefix || "+41");
    setForm((s) => {
      const senderEmpty = !s.sender_phone;
      const pickupEmpty = !s.pickup_contact_phone;
      return {
        ...s,
        sender_name: s.sender_name || user.name || "",
        sender_phone: senderEmpty ? userSender.number : s.sender_phone,
        sender_phone_prefix: senderEmpty ? userSender.prefix : (s.sender_phone_prefix || "+41"),
        sender_company: s.sender_company || user.business_name || "",
        // Pickup contact defaults to sender (user can override)
        pickup_contact_name: s.pickup_contact_name || user.name || "",
        pickup_contact_phone: pickupEmpty ? userSender.number : s.pickup_contact_phone,
        pickup_phone_prefix: pickupEmpty ? userSender.prefix : (s.pickup_phone_prefix || "+41"),
      };
    });
  }, [user]);

  // Phase 44.11 — Memoize map preview props (stable refs) per evitare wasted
  // renders dentro OrderTrackingMap durante la digitazione del checkout.
  const checkoutMapPickup = useMemo(
    () => (Number.isFinite(form.pickup_lat) && Number.isFinite(form.pickup_lng)
      ? { lat: form.pickup_lat, lng: form.pickup_lng } : null),
    [form.pickup_lat, form.pickup_lng],
  );
  const checkoutMapDelivery = useMemo(
    () => (Number.isFinite(form.delivery_lat) && Number.isFinite(form.delivery_lng)
      ? { lat: form.delivery_lat, lng: form.delivery_lng } : null),
    [form.delivery_lat, form.delivery_lng],
  );

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setVal = (k, v) => setForm((s) => ({
    ...s,
    ...(k === "delivery_type" ? selectDeliveryService(s, v)
      : k === "package_size" ? selectParcelCategory(s, v) : { [k]: v }),
  }));

  // Compute current effective time_slot + is_weekend based on form
  const effective = useMemo(() => {
    const ref = form.when_mode === "scheduled" && form.requested_pickup_at
      ? fromSwissLocalInput(form.requested_pickup_at)
      : new Date(currentTime);
    return {
      ref,
      ...swissServiceTime(ref),
      iso: ref?.toISOString() || null,
    };
  }, [form.when_mode, form.requested_pickup_at, currentTime]);

  // Phase 44.04 — Helper: compone un address string dai campi struct quando
  // form.pickup/delivery_address è vuoto (es. l'utente digita senza usare
  // l'autocomplete). Permette al fetchQuote + geocode-fallback di funzionare
  // anche se Google Places JS SDK fallisce nel browser.
  const composeAddressFromStruct = (struct) => {
    if (!struct) return "";
    const street = (struct.street || "").trim();
    const hn = (struct.house_number || "").trim();
    const zip = (struct.zip || "").trim();
    const city = (struct.city || "").trim();
    if (!street || !zip || !city) return "";
    const first = (street + (hn ? " " + hn : "")).trim();
    return [first, `${zip} ${city}`, struct.canton, struct.country].filter(Boolean).join(", ");
  };
  const pickupAddrEffective = form.pickup_address || composeAddressFromStruct(form.pickup_struct);
  const deliveryAddrEffective = form.delivery_address || composeAddressFromStruct(form.delivery_struct);

  const fetchQuote = async (isCurrent) => {
    setQuote(null);
    if (!effective.isOpen || (form.when_mode === "scheduled" && !effective.iso)) {
      setLoadingQuote(false);
      return;
    }
    setLoadingQuote(true);
    try {
      // Phase 44.04 — Fallback geocoding lato server quando l'autocomplete client
      // non ha popolato lat/lng (es. Google Places REQUEST_DENIED). Mantiene
      // l'esperienza fluida anche in ambienti con chiave Maps limitata.
      let pickup = { lat: form.pickup_lat, lng: form.pickup_lng };
      let delivery = { lat: form.delivery_lat, lng: form.delivery_lng };
      const pickupAddr = pickupAddrEffective;
      const deliveryAddr = deliveryAddrEffective;
      if ((pickup.lat == null || pickup.lng == null) && pickupAddr) {
        try {
          const { data: g } = await api.post("/maps/geocode", { address: pickupAddr });
          if (!isCurrent()) return;
          if (g?.found && typeof g.lat === "number" && typeof g.lng === "number") {
            pickup = { lat: g.lat, lng: g.lng };
            setForm((s) => ({
              ...s,
              pickup_lat: g.lat,
              pickup_lng: g.lng,
              pickup_address: s.pickup_address || pickupAddr,
            }));
          }
        } catch { /* keep null, backend will 422 with chiaro detail */ }
      }
      if (!isCurrent()) return;
      if ((delivery.lat == null || delivery.lng == null) && deliveryAddr) {
        try {
          const { data: g } = await api.post("/maps/geocode", { address: deliveryAddr });
          if (!isCurrent()) return;
          if (g?.found && typeof g.lat === "number" && typeof g.lng === "number") {
            delivery = { lat: g.lat, lng: g.lng };
            setForm((s) => ({
              ...s,
              delivery_lat: g.lat,
              delivery_lng: g.lng,
              delivery_address: s.delivery_address || deliveryAddr,
            }));
          }
        } catch { /* keep null */ }
      }
      if (!isCurrent()) return;
      const payload = {
        pickup_address: pickupAddr,
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        delivery_address: deliveryAddr,
        delivery_lat: delivery.lat,
        delivery_lng: delivery.lng,
        delivery_type: form.delivery_type,
        package_size: form.package_size,
        time_slot: effective.time_slot,
        requested_pickup_at: form.when_mode === "scheduled" && form.requested_pickup_at ? effective.iso : null,
      };
      const { data } = await api.post("/quote", payload);
      if (isCurrent()) setQuote(data);
    } catch (e) {
      if (!isCurrent()) return;
      // Errori chiari per il calcolo prezzo (no più "Si è verificato un errore")
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg;
      if (detail) {
        msg = formatApiError(detail);
      } else if (status === 0 || e?.code === "ERR_NETWORK") {
        msg = "Impossibile calcolare il prezzo: nessuna connessione. Controlla la rete.";
      } else if (status >= 500) {
        msg = "Calcolo prezzo non disponibile: server non raggiungibile. Riprova fra qualche secondo.";
      } else {
        msg = e?.message || "Calcolo prezzo non riuscito.";
      }
      toast.error(msg);
      setQuote(null);
      console.error("[NewOrder][fetchQuote] status=", status, "detail=", detail, "raw=", e);
    } finally {
      if (isCurrent()) setLoadingQuote(false);
    }
  };

  useEffect(() => {
    // Phase 44.04 — guard rilassato: triggera quando abbiamo abbastanza
    // informazioni per geocoding fallback (text address o struct completo).
    let current = true;
    if ((step === 4 || step === 5) && pickupAddrEffective && deliveryAddrEffective) {
      fetchQuote(() => current);
    } else {
      setQuote(null);
      setLoadingQuote(false);
    }
    return () => { current = false; };
    // eslint-disable-next-line
  }, [form.delivery_type, form.package_size, form.when_mode, form.requested_pickup_at, step, pickupAddrEffective, deliveryAddrEffective, effective.isOpen, effective.time_slot, effective.is_weekend]);

  const onConfirm = async () => {
    if (["van", "express"].includes(form.delivery_type) || form.package_size === "xl" || quote?.requires_manual_review === true) {
      toast.error(`${reviewLabel} richiede la verifica e la conferma del team prima di prenotare o pagare. Usa Richiedi verifica ${reviewLabel}.`);
      return;
    }
    // Phase 43.13 — Pre-flight validation per messaggi specifici (no più "Si è verificato un errore")
    const missing = [];
    // Coordinates may be absent in the browser: the server resolves and verifies
    // both full addresses again before inserting or funding the order.
    if (!pickupAddrEffective) missing.push("indirizzo di ritiro completo (via, NPA, città)");
    if (!deliveryAddrEffective) missing.push("indirizzo di consegna completo (via, NPA, città)");
    if (!form.pickup_contact_name?.trim()) missing.push("nome di chi consegna il pacco");
    if (!form.pickup_contact_phone?.trim()) missing.push("telefono ritiro");
    if (!form.delivery_contact_name?.trim()) missing.push("nome destinatario");
    if (!form.delivery_contact_phone?.trim()) missing.push("telefono consegna");
    if (!form.package_description?.trim()) missing.push("descrizione del pacco");
    if (!form.sender_name?.trim()) missing.push("nome mittente");
    if (!form.sender_phone?.trim()) missing.push("telefono mittente");
    if (missing.length) {
      toast.error(`Manca: ${missing.join(" · ")}`);
      return;
    }
    // Phase 43.32 — `final_price === 0` è valido (ordine interamente coperto dall'abbonamento).
    // Controllo solo che il quote esista e abbia un final_price numerico definito.
    const fp = quote?.final_price;
    const now = swissServiceTime(new Date());
    if (form.when_mode === "now" && (now.isOpen !== effective.isOpen || now.time_slot !== effective.time_slot || now.is_weekend !== effective.is_weekend)) {
      setCurrentTime(Date.now());
      toast.error("La fascia di servizio è cambiata. Verifica il prezzo aggiornato e l'orario prima di confermare.");
      return;
    }
    if (!effective.isOpen || (form.when_mode === "scheduled" && (!effective.iso || effective.ref <= new Date()))) {
      toast.error(`Scegli un orario di ritiro valido. ${SERVICE_HOURS}.`);
      return;
    }
    if (!quote || !quoteTaxReady || fp === null || fp === undefined || Number.isNaN(Number(fp))) {
      toast.error("Il prezzo non è ancora stato calcolato. Attendi qualche secondo e riprova.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        pickup_address: pickupAddrEffective,
        pickup_lat: form.pickup_lat,
        pickup_lng: form.pickup_lng,
        pickup_struct: form.pickup_struct,
        pickup_contact_name: form.pickup_contact_name.trim(),
        pickup_contact_phone: form.pickup_contact_phone.trim(),
        pickup_phone_prefix: form.pickup_phone_prefix || "+41",
        delivery_address: deliveryAddrEffective,
        delivery_lat: form.delivery_lat,
        delivery_lng: form.delivery_lng,
        delivery_struct: form.delivery_struct,
        delivery_contact_name: form.delivery_contact_name.trim(),
        delivery_contact_phone: form.delivery_contact_phone.trim(),
        delivery_phone_prefix: form.delivery_phone_prefix || "+41",
        sender_name: form.sender_name.trim() || undefined,
        sender_phone: form.sender_phone.trim() || undefined,
        sender_phone_prefix: form.sender_phone_prefix || undefined,
        sender_company: form.sender_company.trim() || undefined,
        package_description: form.package_description,
        package_size: form.package_size,
        delivery_type: form.delivery_type,
        time_slot: effective.time_slot,
        requested_pickup_at: form.when_mode === "scheduled" && form.requested_pickup_at ? effective.iso : null,
        notes: form.notes,
      };
      const { data } = await api.post("/orders", payload);
      setOrderId(data.id);
      setCreatedOrder(data);
      toast.success("Ordine creato!");
      setStep(6);
    } catch (e) {
      // Errori chiari e specifici: niente più "Si è verificato un errore" generico
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg;
      if (detail) {
        msg = formatApiError(detail);
      } else if (status === 0 || e?.code === "ERR_NETWORK") {
        msg = "Connessione assente. Controlla la rete e riprova.";
      } else if (status === 401) {
        msg = "Sessione scaduta. Effettua di nuovo l'accesso.";
      } else if (status === 403) {
        msg = "Operazione non consentita per il tuo account.";
      } else if (status === 422) {
        msg = "Dati ordine incompleti o non validi. Verifica indirizzi e contatti.";
      } else if (status >= 500) {
        msg = "Il server non risponde correttamente. Attendi qualche secondo e riprova.";
      } else {
        msg = e?.message || "Errore sconosciuto. Riprova o contatta il supporto.";
      }
      toast.error(msg);
      // Log dettagliato in console per debug rapido (non visibile all'utente)
      console.error("[NewOrder][onConfirm] status=", status, "detail=", detail, "raw=", e);
    } finally {
      setSubmitting(false);
    }
  };

  const onSimulatePay = async () => {
    if (!orderId) return;
    setPaying(true);
    try {
      await api.post(`/orders/${orderId}/pay`);
      toast.success("Pagamento confermato (simulato)");
      navigate(`/app/orders/${orderId}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(false);
    }
  };

  // Phase 43.18c — Pagamento reale Stripe: crea checkout session e redirect.
  const onStripeCheckout = async () => {
    if (!orderId) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/payment/checkout-session`);
      if (data?.already_paid) {
        toast.success("Ordine già pagato.");
        navigate(`/app/orders/${orderId}`, { replace: true });
        return;
      }
      if (!data?.url) {
        toast.error("Impossibile generare il link di pagamento. Riprova.");
        setPaying(false);
        return;
      }
      // Redirect verso checkout.stripe.com
      window.location.href = data.url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && (detail?.code === "stripe_active" || /stripe.*config/i.test(detail?.message || ""))) {
        // Già configurato → retry forzato (caso raro: lo stato locale era out-of-sync)
        toast.error("Stripe attivo: ricarica la pagina e riprova.");
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
      setPaying(false);
    }
  };

  const stepValid = (s) => {
    if (s === 1) {
      return (
        isStructuredAddressValid(form.pickup_struct) &&
        isStructuredAddressValid(form.delivery_struct) &&
        form.pickup_contact_name.trim().length >= 1 &&
        PHONE_RE.test(form.pickup_contact_phone.trim()) &&
        form.delivery_contact_name.trim().length >= 1 &&
        PHONE_RE.test(form.delivery_contact_phone.trim()) &&
        form.sender_name.trim().length >= 1 &&
        PHONE_RE.test(form.sender_phone.trim())
      );
    }
    if (s === 2) return form.package_description.trim();
    if (s === 3) {
      if (!effective.isOpen) return false;
      if (form.when_mode === "now") return true;
      return !!effective.iso && effective.ref > new Date();
    }
    return true;
  };

  // Phase 43.32 — Dettaglio dei campi mancanti per messaggio chiaro all'utente
  const missingFieldsForStep = (s) => {
    const out = [];
    if (s === 1) {
      const ps = form.pickup_struct || {};
      const ds = form.delivery_struct || {};
      const missingPickup = [];
      if (!(ps.street || "").trim()) missingPickup.push("via");
      if (!(ps.house_number || "").toString().trim()) missingPickup.push("numero civico");
      if (!(ps.zip || "").toString().trim()) missingPickup.push("NPA");
      if (!(ps.city || "").trim()) missingPickup.push("città");
      if (missingPickup.length) out.push(`Indirizzo ritiro: ${missingPickup.join(", ")}`);

      const missingDelivery = [];
      if (!(ds.street || "").trim()) missingDelivery.push("via");
      if (!(ds.house_number || "").toString().trim()) missingDelivery.push("numero civico");
      if (!(ds.zip || "").toString().trim()) missingDelivery.push("NPA");
      if (!(ds.city || "").trim()) missingDelivery.push("città");
      if (missingDelivery.length) out.push(`Indirizzo consegna: ${missingDelivery.join(", ")}`);

      if (!form.pickup_contact_name.trim()) out.push("Nome di chi consegna il pacco");
      if (!PHONE_RE.test(form.pickup_contact_phone.trim())) out.push("Telefono ritiro valido");
      if (!form.delivery_contact_name.trim()) out.push("Nome destinatario");
      if (!PHONE_RE.test(form.delivery_contact_phone.trim())) out.push("Telefono destinatario valido");
      if (!form.sender_name.trim()) out.push("Nome mittente");
      if (!PHONE_RE.test(form.sender_phone.trim())) out.push("Telefono mittente valido");
    }
    if (s === 2 && !form.package_description.trim()) {
      out.push("Descrizione del pacco");
    }
    if (s === 3 && form.when_mode === "scheduled" && !form.requested_pickup_at) {
      out.push("Data e ora del ritiro");
    }
    return out;
  };

  return (
    <div className="space-y-6" data-testid="customer-new-order">
      {wrongRole && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 sm:p-8" data-testid="new-order-wrong-role">
          <h2 className="font-display text-xl font-bold tracking-tight text-amber-950">
            Account non abilitato a creare ordini
          </h2>
          <p className="mt-2 text-sm text-amber-900">
            Sei loggato come <strong>{user?.role || "non riconosciuto"}</strong>{" "}
            ({user?.email}). Solo i clienti (privato, azienda o negozio) possono creare nuovi ordini.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => navigate(user?.role === "driver" ? "/driver" : user?.role === "admin" ? "/admin" : "/")}
              data-testid="back-to-dashboard"
              className="h-10 px-4 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition"
            >
              Torna alla dashboard
            </button>
            <button
              onClick={() => { try { localStorage.removeItem("token"); } catch (e) { /* ignore */ } navigate("/login"); }}
              data-testid="logout-and-login"
              className="h-10 px-4 rounded-xl border border-amber-300 bg-white text-sm font-semibold hover:bg-amber-100/60 transition"
            >
              Esci e accedi come cliente
            </button>
          </div>
        </div>
      )}
      <div>
        <p className="overline">Nuova consegna</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Crea ordine</h1>
        <p className="text-xs text-dapnow-muted mt-1">{SERVICE_HOURS}. Consegne con driver interni approvati.</p>
      </div>

      <div className="flex gap-2">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${step >= i ? "bg-dapnow-blue" : "bg-dapnow-line"}`} />
        ))}
      </div>

      {step === 1 && (
        <section className="card-soft p-5 sm:p-7 space-y-6">
          <Header icon={MapPin} title="Indirizzi e contatti" desc="Specifica i punti di ritiro/consegna e i contatti del mittente e del destinatario (obbligatori)." />

          {/* Phase 32 Batch A — Mittente (precompilato dal profilo, modificabile) */}
          <div className="rounded-2xl border border-dapnow-line p-4 space-y-3 min-w-0 overflow-hidden" data-testid="sender-block">
            <p className="overline">Mittente (tu)</p>
            <div className="grid sm:grid-cols-2 gap-3 min-w-0">
              <div className="min-w-0">
                <label className="text-xs font-semibold tracking-tight">Nome <span className="text-rose-600">*</span></label>
                <input data-testid="sender-name" value={form.sender_name} onChange={set("sender_name")} className="input-tall mt-1 w-full max-w-full" placeholder="Mario Rossi" />
              </div>
              <div className="min-w-0">
                <PhonePrefixInput
                  label={<>Telefono mittente <span className="text-rose-600">*</span></>}
                  testidPrefix="sender-phone"
                  prefix={form.sender_phone_prefix}
                  onPrefixChange={(p) => setVal("sender_phone_prefix", p)}
                  number={form.sender_phone}
                  onNumberChange={(n) => setVal("sender_phone", n)}
                  required
                />
              </div>
              <div className="sm:col-span-2 min-w-0">
                <label className="text-xs font-semibold tracking-tight">Azienda / Ragione sociale (opz.)</label>
                <input data-testid="sender-company" value={form.sender_company} onChange={set("sender_company")} className="input-tall mt-1 w-full max-w-full" placeholder="Ragione sociale della tua azienda" />
              </div>
            </div>
          </div>

          {/* Ritiro */}
          <div className="rounded-2xl border border-dapnow-line p-4 space-y-4">
            <StructuredAddressForm
              value={{ ...(form.pickup_struct || {}), address: form.pickup_address, lat: form.pickup_lat, lng: form.pickup_lng }}
              onChange={(next) => setForm((s) => ({
                ...s,
                pickup_address: next.address || "",
                pickup_lat: typeof next.lat === "number" ? next.lat : null,
                pickup_lng: typeof next.lng === "number" ? next.lng : null,
                pickup_struct: { ...s.pickup_struct, ...next, address: undefined, lat: undefined, lng: undefined },
              }))}
              testidPrefix="pickup"
              legendLabel="Indirizzo di ritiro"
              showCompany
            />
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-dapnow-line">
              <div>
                <label className="text-xs font-semibold tracking-tight">Nome mittente al ritiro *</label>
                <input data-testid="new-pickup-contact-name" value={form.pickup_contact_name} onChange={set("pickup_contact_name")} className="input-tall mt-1 w-full" placeholder="Chi consegna il pacco al driver" />
              </div>
              <PhonePrefixInput
                label="Telefono ritiro *"
                testidPrefix="new-pickup-contact-phone"
                prefix={form.pickup_phone_prefix}
                onPrefixChange={(p) => setVal("pickup_phone_prefix", p)}
                number={form.pickup_contact_phone}
                onNumberChange={(n) => setVal("pickup_contact_phone", n)}
                required
              />
            </div>
          </div>

          {/* Consegna */}
          <div className="rounded-2xl border border-dapnow-line p-4 space-y-4">
            <StructuredAddressForm
              value={{ ...(form.delivery_struct || {}), address: form.delivery_address, lat: form.delivery_lat, lng: form.delivery_lng }}
              onChange={(next) => setForm((s) => ({
                ...s,
                delivery_address: next.address || "",
                delivery_lat: typeof next.lat === "number" ? next.lat : null,
                delivery_lng: typeof next.lng === "number" ? next.lng : null,
                delivery_struct: { ...s.delivery_struct, ...next, address: undefined, lat: undefined, lng: undefined },
              }))}
              testidPrefix="delivery"
              legendLabel="Indirizzo di consegna"
              showCompany
            />
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-dapnow-line">
              <div>
                <label className="text-xs font-semibold tracking-tight">Nome destinatario *</label>
                <input data-testid="new-delivery-contact-name" value={form.delivery_contact_name} onChange={set("delivery_contact_name")} className="input-tall mt-1 w-full" placeholder="Chi riceve il pacco" />
              </div>
              <PhonePrefixInput
                label="Telefono destinatario *"
                testidPrefix="new-delivery-contact-phone"
                prefix={form.delivery_phone_prefix}
                onPrefixChange={(p) => setVal("delivery_phone_prefix", p)}
                number={form.delivery_contact_phone}
                onNumberChange={(n) => setVal("delivery_contact_phone", n)}
                required
              />
            </div>
          </div>

          <p className="text-xs text-dapnow-muted">I contatti permettono al driver di avvisare all'arrivo e ridurre i tentativi di consegna falliti.</p>
          <CrossBorderNotice
            pickupCountry={form.pickup_struct?.country}
            deliveryCountry={form.delivery_struct?.country}
          />
          <Step1MissingNotice items={missingFieldsForStep(1)} />
          <div className="flex justify-end">
            <button data-testid="new-step1-next" disabled={!stepValid(1)} onClick={() => setStep(2)} className="btn-primary">
              Continua <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="card-soft p-5 sm:p-7 space-y-5">
          <Header icon={Package} title="Pacco" desc="Descrivi cosa stai spedendo." />
          <div>
            <label className="text-sm font-semibold tracking-tight">Descrizione <span className="text-rose-600">*</span></label>
            <input data-testid="new-package-description" value={form.package_description} onChange={set("package_description")} className="input-tall mt-1.5 w-full max-w-full" placeholder="Pacco con documenti, libri, ecc." required />
            <p className="text-[11px] text-dapnow-muted mt-1">* Campo obbligatorio</p>
          </div>
          <div>
            <p className="overline mb-2">Dimensione</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SIZES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`new-size-${s.id}`}
                  onClick={() => setVal("package_size", s.id)}
                  className={`text-left p-4 rounded-xl border transition relative ${form.package_size === s.id ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
                >
                  {s.needsApproval && (
                    <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                      <AlertTriangle className="h-2.5 w-2.5" /> Da confermare
                    </span>
                  )}
                  <p className="font-display text-base font-semibold tracking-tight">{s.label}</p>
                  <p className="text-xs text-dapnow-muted mt-0.5">{s.desc}</p>
                  <p className="text-[11px] text-dapnow-ink font-medium mt-2">{s.specs}</p>
                  <p className="text-[11px] text-dapnow-muted mt-0.5">{s.vehicle}</p>
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-dapnow-muted">Limiti per singolo collo, imballaggio incluso. Se il pacco supera un limite o richiede movimentazione particolare, contatta DapNow prima di prenotare.</p>
            {form.package_size === "xl" && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2.5" data-testid="xl-needs-approval-banner">
                <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900">
                  <p className="font-semibold">Pacco da confermare manualmente</p>
                  <p>Van: massimo 25 kg e 120 × 80 × 80 cm per collo, imballaggio incluso. Il team deve verificare peso, dimensioni, accessi e movimentazione prima della prenotazione e del pagamento.</p>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold tracking-tight">Note (opz.)</label>
            <textarea data-testid="new-notes" value={form.notes} onChange={set("notes")} rows={2} className="mt-1.5 w-full rounded-xl border border-dapnow-line p-3 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none" placeholder="Citofono, piano, indicazioni…" />
          </div>
          <Step1MissingNotice items={missingFieldsForStep(2)} />
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Indietro</button>
            <button data-testid="new-step2-next" disabled={!stepValid(2)} onClick={() => setStep(3)} className="btn-primary">
              Continua <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="card-soft p-5 sm:p-7 space-y-5">
          <Header icon={Clock} title="Quando ritirare" desc={`Scegli quando richiedere il ritiro. ${SERVICE_HOURS}.`} />
          <p className="text-sm text-dapnow-muted">La fascia riguarda il ritiro, non la consegna. Standard: consegna entro le 21:00 per ritiri entro le 18:00. Per ritiri successivi, contatta DapNow per concordare i tempi prima di prenotare.</p>
          {!effective.isOpen && <p role="alert" className="text-sm text-amber-800">Il ritiro deve avvenire tra le 06:30 e le 21:00. Scegli un orario nella fascia di servizio.</p>}

          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="new-when-now"
              onClick={() => setVal("when_mode", "now")}
              className={`text-left p-5 rounded-2xl border transition ${form.when_mode === "now" ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
            >
              <div className="flex items-center gap-3">
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${form.when_mode === "now" ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                  <Clock className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-lg font-semibold tracking-tight">Adesso</p>
                  <p className="text-xs text-dapnow-muted">Richiedi il ritiro appena disponibile. Fascia di ritiro: <strong>{currentSlotLabel(effective.time_slot)}</strong></p>
                </div>
              </div>
            </button>

            <button
              type="button"
              data-testid="new-when-scheduled"
              onClick={() => setVal("when_mode", "scheduled")}
              className={`text-left p-5 rounded-2xl border transition ${form.when_mode === "scheduled" ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
            >
              <div className="flex items-center gap-3">
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${form.when_mode === "scheduled" ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                  <CalendarClock className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-lg font-semibold tracking-tight">Programmato</p>
                  <p className="text-xs text-dapnow-muted">Scegli data e ora di ritiro.</p>
                </div>
              </div>
            </button>
          </div>

          {form.when_mode === "scheduled" && (
            <div className="rounded-2xl bg-dapnow-bg p-5 space-y-3">
              <Field label="Data e ora di ritiro (ora svizzera)" required>
                <input
                  data-testid="new-scheduled-datetime"
                  type="datetime-local"
                  value={form.requested_pickup_at}
                  onChange={set("requested_pickup_at")}
                  className="input-tall w-full"
                />
              </Field>
              <p className="text-xs text-dapnow-muted">Fascia di ritiro rilevata: <strong>{currentSlotLabel(effective.time_slot)}</strong>{effective.is_weekend ? " · weekend (+30%)" : ""}</p>
            </div>
          )}

          <div>
            <p className="overline mb-2">Fasce di ritiro e maggiorazioni</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {TIME_SLOT_OPTIONS.map((s) => {
                const active = effective.time_slot === s.id;
                return (
                  <div
                    key={s.id}
                    data-testid={`time-slot-${s.id}`}
                    className={`p-4 rounded-2xl border ${active ? "border-dapnow-blue bg-dapnow-blue-soft" : "border-dapnow-line bg-white"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${active ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                        <s.icon className="h-4 w-4" />
                      </span>
                      <p className="font-display text-base font-semibold">{s.label}</p>
                    </div>
                    <p className="text-xs text-dapnow-muted mt-2">{s.time}</p>
                    <p className={`text-sm font-semibold mt-1 ${s.id === "day" ? "text-emerald-700" : "text-dapnow-red"}`}>{s.surcharge}</p>
                    {s.badge && <p className="text-[11px] text-amber-700 mt-1">{s.badge}</p>}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-dapnow-muted mt-2">Weekend e festivi: +30% addizionale (si somma alla maggiorazione della fascia di ritiro).</p>
          </div>

          {(effective.time_slot !== "day" || effective.is_weekend) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="out-of-hours-warning">
              <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <p className="font-semibold">Disponibilità driver da confermare.</p>
                <p>Per gli ordini al mattino presto, alla sera o nel weekend, ti avviseremo appena un driver interno accetta la consegna.</p>
              </div>
            </div>
          )}

          <Step1MissingNotice items={missingFieldsForStep(3)} />
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Indietro</button>
            <button data-testid="new-step3-next" disabled={!stepValid(3)} onClick={() => setStep(4)} className="btn-primary">
              Continua <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="card-soft p-5 sm:p-7 space-y-5">
          <Header icon={Truck} title="Tipo di servizio" desc="Standard con flotta interna; Express e Van su richiesta, dopo verifica e conferma del team. Shared con driver privati sarà disponibile in futuro." />
          <p className="text-xs text-dapnow-muted" data-testid="service-price-basis">{business ? "Tariffe base IVA esclusa. Il preventivo indica l’IVA e il totale effettivo prima della conferma." : "Tariffe per privati IVA inclusa. Il preventivo indica il totale prima della conferma."}</p>
          {form.delivery_type === "shared" && (
            <SharedSustainabilityBadge variant="soft" />
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {DELIVERY_OPTIONS.map((o) => {
              const active = form.delivery_type === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`new-delivery-type-${o.id}`}
                  onClick={() => setVal("delivery_type", o.id)}
                  disabled={o.id === "shared"}
                  aria-pressed={active}
                  className={`text-left p-5 rounded-2xl border transition disabled:bg-zinc-50 disabled:cursor-not-allowed ${active ? "border-dapnow-blue bg-dapnow-blue-soft shadow-sm" : "border-dapnow-line bg-white hover:border-dapnow-blue/40"}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${active ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                      <o.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                        {o.label}
                        {o.id === "shared" && <SharedSustainabilityBadge variant="inline" />}
                      </p>
                      <p className="text-xs text-dapnow-muted">{o.id === "shared" ? "In arrivo · non prenotabile" : `${o.base} • ${o.min}`}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-dapnow-muted">{o.desc}</p>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-dapnow-muted">Pacco selezionato: <strong>{SIZES.find(p => p.id === form.package_size)?.label} · {SIZES.find(p => p.id === form.package_size)?.specs}</strong>. Controlla che peso e dimensioni rientrino nei limiti. Shared: <a href="/become-driver" className="text-dapnow-blue underline">richiedi informazioni</a>.</p>
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn-ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Indietro</button>
            <button data-testid="new-step4-next" onClick={() => setStep(5)} className="btn-primary">
              {requiresManualReview ? `Vedi stima ${reviewLabel}` : "Vedi prezzo"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {step === 5 && (
        <section className="card-soft overflow-hidden">
          {/* Phase 43.13 — Mappa reale del percorso (fix coords + fallback elegante) */}
          {form.pickup_lat && form.pickup_lng && form.delivery_lat && form.delivery_lng ? (
            <div data-testid="checkout-route-preview" className="border-b border-dapnow-line">
              <OrderTrackingMap
                pickup={checkoutMapPickup}
                delivery={checkoutMapDelivery}
                className="h-44 sm:h-52 w-full"
              />
            </div>
          ) : (
            <div
              data-testid="checkout-route-fallback"
              className="px-6 py-5 border-b border-dapnow-line bg-dapnow-bg flex items-start gap-3"
            >
              <MapPin className="h-5 w-5 text-dapnow-muted shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold tracking-tight">Anteprima mappa non disponibile</p>
                <p className="text-dapnow-muted text-xs mt-0.5">
                  {requiresManualReview ? `Il percorso e la disponibilità saranno verificati dal team prima di concordare la consegna ${reviewLabel}.` : "Le coordinate degli indirizzi non sono complete. Procedi pure: il percorso reale verrà calcolato dopo la conferma dell'ordine."}
                </p>
              </div>
            </div>
          )}
          <div className="p-6 sm:p-7 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Riepilogo</p>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">{requiresManualReview ? `Stima ${reviewLabel} · verifica preventiva` : "Conferma il tuo ordine"}</h2>
              </div>
              <span className="pill bg-dapnow-blue-soft text-dapnow-blue">{DELIVERY_TYPE_LABEL[form.delivery_type]}</span>
            </div>

            {form.delivery_type === "shared" && (
              <SharedSustainabilityBadge variant="soft" />
            )}

            {loadingQuote ? (
              <div className="h-32 shimmer rounded-xl" />
            ) : quote ? (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Stat label="Distanza" value={formatKm(quote.distance_km)} />
                  <Stat label="Fascia" value={`${quote.time_slot_name}${quote.is_weekend ? " · weekend" : ""}`} />
                </div>

                <div className="rounded-2xl bg-dapnow-bg p-5 space-y-2 text-sm" data-testid="price-breakdown">
                  {quoteTax?.audience === "business" && <p className="text-xs text-dapnow-muted">Tariffe e supplementi IVA esclusa</p>}
                  <Row k={`Tariffa base (${quote.delivery_type_name})`} v={formatCHF(quote.base_price)} />
                  <Row k={`Costo distanza ${formatKm(quote.distance_km)} × ${formatCHF(quote.price_per_km)}/km`} v={formatCHF(quote.distance_cost)} />
                  <Row k={`Moltiplicatore pacco (${SIZE_LABEL[quote.package_size]} ×${quote.package_multiplier.toFixed(2)})`} v={formatCHF(Math.max(0, quote.subtotal_pre_surcharge - quote.base_price - quote.distance_cost))} muted />
                  <Row k="Subtotale" v={formatCHF(quote.subtotal_pre_surcharge)} bold />
                  {quote.time_slot_surcharge > 0 && (
                    <Row k={`Supplemento ${quote.time_slot_name} (+${Math.round((quote.time_slot_multiplier - 1) * 100)}%)`} v={`+ ${formatCHF(quote.time_slot_surcharge)}`} accent />
                  )}
                  {quote.weekend_surcharge > 0 && (
                    <Row k={`Supplemento weekend/festivi (+${quote.weekend_surcharge_pct}%)`} v={`+ ${formatCHF(quote.weekend_surcharge)}`} accent />
                  )}
                  {(quoteTax?.audience === "business" ? quoteTax.net_chf : quote.gross_price) === quote.minimum_price && (
                    <Row k="Tariffa minima applicata" v={formatCHF(quote.minimum_price)} muted />
                  )}
                  {quoteTax?.audience === "business"
                    ? <CustomerTaxBreakdown tax={quoteTax} title="Tariffa completa della consegna" testId="quote-price-tax" />
                    : <Row k="Totale tariffa" v={formatCHF(quote.gross_price)} bold />}
                  {!requiresManualReview && quote.included_delivery_consumed && (
                    <>
                      <Row
                        k={`Consegna inclusa nell'abbonamento (fino a ${quote.included_km_per_delivery} km)${netCreditKnown ? " · IVA esclusa" : quoteTax?.audience === "business" ? " · importo registrato" : ""}`}
                        v={`- ${formatCHF(inclusionCredit)}`}
                        accent
                      />
                      <p className="text-xs text-emerald-700" data-testid="included-delivery-note">
                        Restano <strong>{quote.included_remaining}</strong> consegne incluse questo mese.
                      </p>
                      {quote.extra_km_charge > 0 && (
                        <Row
                          k={`Km extra oltre i ${quote.included_km_per_delivery} km`}
                          v={`+ ${formatCHF(quote.extra_km_charge)}`}
                          accent
                        />
                      )}
                      {quote.express_supplement > 0 && (
                        <Row k="Supplemento Express" v={`+ ${formatCHF(quote.express_supplement)}`} accent />
                      )}
                    </>
                  )}
                  {!requiresManualReview && quote.out_of_inclusion_reason === "over_count" && (
                    <p className="text-xs text-amber-700 italic" data-testid="over-count-note">
                      Hai utilizzato tutte le consegne incluse del mese — questa è a tariffa standard.
                    </p>
                  )}
                  {!requiresManualReview && quote.out_of_inclusion_reason === "not_eligible_van" && (
                    <p className="text-xs text-dapnow-muted italic" data-testid="van-not-included-note">
                      Van: prezzo dedicato, non incluso nell'abbonamento.
                    </p>
                  )}
                  {!requiresManualReview && chargeTax?.audience === "business" && <CustomerTaxBreakdown tax={chargeTax} title="Importo dopo le inclusioni" totalLabel="Totale prima del wallet" testId="quote-charge-tax" />}
                  {!requiresManualReview && quote.funding_preview?.wallet_credit_apply_chf > 0 && <Row k="Credito wallet utilizzato" v={`− ${formatCHF(quote.funding_preview.wallet_credit_apply_chf)}`} accent />}
                </div>

                <div className="rounded-2xl bg-dapnow-blue text-white p-5 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="overline text-white/80" data-testid="quote-amount-label">{requiresManualReview ? "Prezzo stimato · da verificare" : "Importo da pagare"}</p>
                    <p className="font-display text-4xl font-extrabold tracking-tighter mt-1" data-testid="final-price">{formatCHF(requiresManualReview ? (quote.gross_price ?? quote.final_price) : amountDue)}</p>
                  </div>
                  <div className="text-right text-xs text-white/80">
                    <p>{quoteTax ? `IVA ${formatVatRate(quoteTax.vat_rate)} inclusa nel totale` : "Totale del preventivo"}</p>
                  </div>
                </div>
                {!quoteTaxReady && <p role="alert" className="text-sm text-amber-800">Il riepilogo IVA completo non è disponibile. Ricalcola il preventivo prima di confermare.</p>}

                {/* Phase 44.04 — Funding preview banner: chiarisce all'utente
                    da dove verrà scalata la corsa prima della conferma. */}
                {!requiresManualReview && quote.funding_preview?.kind === "delivery_pack" && (
                  <div
                    className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/80 p-4 flex items-start gap-3"
                    data-testid="funding-preview-pack"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shrink-0">
                      <PackagePlus className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold tracking-tight text-emerald-900">
                        Coperta dal Credito Consegne
                      </p>
                      <p className="text-xs text-emerald-800 mt-0.5">
                        1 corsa verrà scalata automaticamente.
                        {" "}
                        <strong>Rimarranno {quote.funding_preview.remaining_after} corse bonus.</strong>
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        Nessun pagamento sarà richiesto.
                      </p>
                    </div>
                  </div>
                )}
                {!requiresManualReview && quote.funding_preview?.kind === "subscription" && (
                  <div
                    className="rounded-2xl border-2 border-violet-300 bg-violet-50/80 p-4 flex items-start gap-3"
                    data-testid="funding-preview-subscription"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white shrink-0">
                      <ShieldCheck className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold tracking-tight text-violet-900">
                        Inclusa nel tuo abbonamento
                      </p>
                      <p className="text-xs text-violet-800 mt-0.5">
                        1 consegna verrà scalata dal piano. <strong>Rimarranno {quote.funding_preview.remaining_after} consegne incluse questo mese.</strong>
                      </p>
                    </div>
                  </div>
                )}
                {!requiresManualReview && quote.funding_preview?.kind === "subscription_partial" && (
                  <div
                    className="rounded-2xl border-2 border-violet-300 bg-violet-50/80 p-4 flex items-start gap-3"
                    data-testid="funding-preview-subscription-partial"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white shrink-0">
                      <ShieldCheck className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold tracking-tight text-violet-900">
                        Coperta dall'abbonamento + extra
                      </p>
                      <p className="text-xs text-violet-800 mt-0.5">
                        1 consegna scalata dal piano. L'extra di <strong>{formatCHF(quote.funding_preview.amount_due_chf)}</strong> sarà addebitato sulla carta dell'abbonamento.
                      </p>
                      <p className="text-[11px] text-violet-700 mt-1">
                        Rimarranno {quote.funding_preview.remaining_after} consegne incluse questo mese.
                      </p>
                    </div>
                  </div>
                )}

                {/* Phase 44.12 — Credito DapNow (wallet referral) banner.
                    Visibile sia quando copre interamente l'importo (kind=wallet_credit_full)
                    sia quando copre parzialmente (presenza di wallet_credit_apply_chf). */}
                {!requiresManualReview && quote.funding_preview?.kind === "wallet_credit_full" && (
                  <div
                    className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/80 p-4 flex items-start gap-3"
                    data-testid="funding-preview-wallet-full"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shrink-0">
                      <Sparkles className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold tracking-tight text-emerald-900">
                        Userai {formatCHF(quote.funding_preview.wallet_credit_apply_chf)} di credito DapNow
                      </p>
                      <p className="text-xs text-emerald-800 mt-0.5">
                        Questa consegna è interamente coperta dal tuo credito wallet — niente da pagare.
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        Saldo dopo l'ordine: {formatCHF(quote.funding_preview.wallet_credit_balance_after_chf)}.
                      </p>
                    </div>
                  </div>
                )}
                {!requiresManualReview && quote.funding_preview?.kind === "stripe" && quote.funding_preview?.wallet_credit_apply_chf > 0 && (
                  <div
                    className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-start gap-3"
                    data-testid="funding-preview-wallet-partial"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shrink-0">
                      <Sparkles className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold tracking-tight text-emerald-900">
                        Userai {formatCHF(quote.funding_preview.wallet_credit_apply_chf)} di credito DapNow
                      </p>
                      <p className="text-xs text-emerald-800 mt-0.5">
                        Resta da pagare con carta: <strong>{formatCHF(quote.funding_preview.amount_due_chf)}</strong>
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        Saldo dopo l'ordine: {formatCHF(quote.funding_preview.wallet_credit_balance_after_chf)}.
                      </p>
                    </div>
                  </div>
                )}

                {!requiresManualReview && quote.included_delivery_consumed && (
                  <SubscriptionBreakdown data={quote} />
                )}

                {!requiresManualReview && quote.is_out_of_hours && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900">Disponibilità driver da confermare. Ti avviseremo appena un trasportatore accetta la consegna.</p>
                  </div>
                )}
              </>
            ) : <p role="alert" className="text-sm text-amber-800">{requiresManualReview ? "Stima non disponibile. Torna al servizio e riprova oppure contatta DapNow." : "Preventivo non disponibile. Torna al servizio e riprova prima di confermare l'ordine."}</p>}

            {requiresManualReview && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2 text-sm text-amber-900" data-testid={`new-${reviewService}-review-note`}>
                <p className="font-semibold">Su richiesta · verifica preventiva</p>
                <p>{typeof quote?.manual_review_message === "string" ? quote.manual_review_message : reviewDescription}</p>
                {reviewService === "express" && <p>La consegna deve concludersi entro le 21:00; la stima non estende l’orario di servizio.</p>}
                <p>{parcel?.specs} per collo, imballaggio incluso. Nessun ordine, pagamento o utilizzo di crediti viene effettuato da questa stima.</p>
                <p>Si apre una bozza email da inviare. La richiesta non è una prenotazione né un’approvazione.</p>
                <a href={`tel:+${SUPPORT_PHONE_RAW}`} className="inline-block font-semibold underline" data-testid={`new-${reviewService}-phone`}>Chiama DapNow: {SUPPORT_PHONE}</a>
              </div>
            )}

            <ul className="space-y-2 text-sm">
              <li className="flex justify-between"><span className="text-dapnow-muted">Da</span><span className="font-medium truncate ml-3">{pickupAddrEffective} ({form.pickup_contact_name})</span></li>
              <li className="flex justify-between"><span className="text-dapnow-muted">A</span><span className="font-medium truncate ml-3">{deliveryAddrEffective} ({form.delivery_contact_name})</span></li>
              <li className="flex justify-between"><span className="text-dapnow-muted">Pacco</span><span className="font-medium">{SIZE_LABEL[form.package_size]}</span></li>
              <li className="flex justify-between"><span className="text-dapnow-muted">Quando</span><span className="font-medium">{form.when_mode === "now" ? "Adesso" : new Date(form.requested_pickup_at).toLocaleString()}</span></li>
            </ul>

            <div className="flex flex-col sm:flex-row justify-between gap-2">
              <button onClick={() => setStep(4)} className="btn-ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Indietro</button>
              {requiresManualReview ? (
                quote && !loadingQuote ? <a
                  href={informationUrl(`Vorrei richiedere una verifica preventiva per una consegna ${reviewLabel}.\nRitiro: ${pickupAddrEffective}\nConsegna: ${deliveryAddrEffective}\nContenuto: ${form.package_description}\nLimiti per collo, imballaggio incluso: ${parcelLimits}.\n${reviewService === "express" ? `${reviewDescription}.\nOrario di ritiro richiesto: ${form.when_mode === "now" ? "Da concordare" : form.requested_pickup_at}\n` : ""}Peso e dimensioni effettivi:\nAccessi, scale e necessità di movimentazione:\nAttendo la verifica e la conferma del team prima di prenotare o pagare.`)}
                  data-testid={`new-${reviewService}-review-request`} className="btn-primary text-center"
                >Richiedi verifica {reviewLabel}</a> : <button disabled className="btn-primary" data-testid={`new-${reviewService}-review-request`}>Richiedi verifica {reviewLabel}</button>
              ) : <button data-testid="new-confirm-btn" disabled={submitting || loadingQuote || !quote || !quoteTaxReady} onClick={onConfirm} className="btn-primary">
                {submitting ? "Conferma…" : "Conferma ordine"} <ArrowRight className="ml-1.5 h-4 w-4" />
              </button>}
            </div>
          </div>
        </section>
      )}

      {step === 6 && (() => {
        // Phase 44.04 — Step 6 funding-aware:
        // se il backend ha già marcato payment_status='paid' (sub/pack), NO Stripe.
        const co = createdOrder || {};
        const isAlreadyPaid = co.payment_status === "paid";
        const src = co.payment_source;
        const coveredByPack = isAlreadyPaid && src === "delivery_pack";
        const coveredBySub  = isAlreadyPaid && src === "subscription_full_credit";
        const coveredByWallet = isAlreadyPaid && src === "wallet_credit_full";
        const remainingAfter = co.delivery_pack_remaining_after;
        const amountDue = co.final_price ?? co.price;
        const orderChargeTax = validPriceTax(co.charge_tax) ? co.charge_tax : null;
        const paymentBlock = orderPaymentBlock(co);

        if (paymentBlock) return (
          <section className="space-y-4" data-testid="step6-payment-blocked">
            <CheckoutVerification verification={paymentBlock} />
            <button className="btn-ghost w-full" onClick={() => navigate(`/app/orders/${orderId}`)}>Vai all’ordine</button>
          </section>
        );

        if (isAlreadyPaid) {
          const accent = coveredBySub ? "violet" : "emerald";
          const palette = {
            emerald: { bg: "bg-emerald-50", text: "text-emerald-900", textMuted: "text-emerald-800", icon: "bg-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700" },
            violet:  { bg: "bg-violet-50",  text: "text-violet-900",  textMuted: "text-violet-800",  icon: "bg-violet-600",  btn: "bg-violet-600 hover:bg-violet-700" },
          }[accent];
          return (
            <section className="card-soft p-6 sm:p-8 space-y-5" data-testid="step6-covered">
              <div className="flex items-start gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${palette.icon} text-white shrink-0`}>
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="step6-covered-title">Ordine creato</h2>
                  <p className="text-sm text-dapnow-muted mt-0.5">
                    {coveredByPack
                      ? "Questa consegna è coperta dal tuo Credito Consegne."
                      : coveredBySub ? "Questa consegna è inclusa nel tuo abbonamento."
                      : coveredByWallet ? "Questa consegna è stata pagata con il saldo del wallet."
                      : "Il pagamento di questa consegna è già confermato."}
                  </p>
                </div>
              </div>

              <div className={`rounded-2xl ${palette.bg} p-5 space-y-3`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <PackagePlus className={`h-5 w-5 ${palette.text}`} />
                    <p className={`text-sm font-semibold ${palette.text}`}>{coveredByPack || coveredBySub ? "1 corsa utilizzata" : "Pagamento confermato"}</p>
                  </div>
                  <span className={`shrink-0 rounded-full ${palette.icon} text-white px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide`}>
                    Nessun altro pagamento
                  </span>
                </div>
                {coveredByPack && typeof remainingAfter === "number" && (
                  <div className="rounded-xl bg-white/70 p-3 flex items-center justify-between">
                    <span className={`text-xs ${palette.textMuted}`}>Saldo dopo utilizzo</span>
                    <span className={`font-display text-lg font-extrabold ${palette.text}`} data-testid="step6-pack-remaining">
                      {remainingAfter} {remainingAfter === 1 ? "corsa" : "corse"} bonus
                    </span>
                  </div>
                )}
              </div>

              {orderChargeTax?.audience === "business" && <CustomerTaxBreakdown tax={orderChargeTax} title="Riepilogo della consegna" totalLabel="Totale prima del wallet" testId="created-order-tax" />}
              {orderChargeTax && co.credit_balance_applied_chf > 0 && <p className="text-sm">Credito wallet utilizzato: {formatCHF(co.credit_balance_applied_chf)}</p>}

              {/* Phase 44.27 — Card istruzioni etichetta SOPRA "Attiva consegna"
                  (l'utente clicca subito senza scrollare → deve essere visibile). */}
              {orderId && <ParcelLabelCard orderId={orderId} testid="step6-parcel-label-card" />}

              <button
                data-testid="activate-delivery-btn"
                onClick={() => navigate(`/app/orders/${orderId}`, { replace: true })}
                className={`btn-primary w-full ${palette.btn}`}
              >
                Vai all’ordine <ArrowRight className="ml-1.5 h-4 w-4" />
              </button>
            </section>
          );
        }

        // Caso classico: pagamento Stripe richiesto
        return (
          <section className="card-soft p-6 sm:p-8 space-y-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><ShieldCheck className="h-5 w-5" /></span>
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight">Ordine creato</h2>
                <p className="text-sm text-dapnow-muted">Procedi al pagamento per attivare la consegna.</p>
              </div>
            </div>
            <div className="rounded-2xl bg-dapnow-bg p-5 flex items-end justify-between">
              <div>
                <p className="overline">Totale da pagare</p>
                <p className="font-display text-4xl font-extrabold tracking-tighter mt-1">{formatCHF(amountDue)}</p>
              </div>
              {paymentConfig?.payments_available ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold" data-testid="payment-mode-real">
                  <ShieldCheck className="h-3.5 w-3.5" /> {paymentConfig.stripe_mode === "test" ? "Stripe Test" : "Stripe"}
                </span>
              ) : paymentConfig?.simulation_enabled ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-semibold" data-testid="payment-mode-simulated">
                  <Receipt className="h-3.5 w-3.5" /> SIMULATO
                </span>
              ) : <span className="text-xs text-dapnow-muted">Pagamenti in preparazione</span>}
            </div>
            {orderChargeTax?.audience === "business" && <CustomerTaxBreakdown tax={orderChargeTax} title="Riepilogo della consegna" totalLabel="Totale prima del wallet" testId="created-order-tax" />}
            {orderChargeTax && co.credit_balance_applied_chf > 0 && <p className="text-sm">Credito wallet utilizzato: {formatCHF(co.credit_balance_applied_chf)}</p>}
            <p className="text-xs text-dapnow-muted">
              {paymentConfig?.payments_available
                ? paymentConfig.stripe_mode === "test"
                  ? "Ambiente di collaudo Stripe: usa una carta di prova. Questo pagamento non acquista un servizio reale."
                  : "Verrai reindirizzato a Stripe per inserire i dati della carta in modo sicuro."
                : paymentConfig?.simulation_enabled
                  ? "Pagamento simulato abilitato per il collaudo. Non acquista un servizio reale."
                  : "I pagamenti non sono ancora disponibili. Il tuo ordine resta in attesa di pagamento."}
            </p>
            <div className="flex flex-col gap-3 pt-1">
              {paymentConfig?.payments_available ? (
                <button
                  data-testid="stripe-checkout-btn"
                  onClick={onStripeCheckout}
                  disabled={paying}
                  className="btn-primary w-full"
                >
                  {paying ? "Apro Stripe…" : `Paga con carta · ${formatCHF(amountDue)}`}
                </button>
              ) : paymentConfig?.simulation_enabled ? (
                <button data-testid="simulate-pay-btn" onClick={onSimulatePay} disabled={paying} className="btn-primary w-full">
                  {paying ? "Elaborazione…" : "Paga ora (simulato)"}
                </button>
              ) : null}
              <button onClick={() => navigate(`/app/orders/${orderId}`)} className="btn-ghost w-full" data-testid="skip-pay-btn">
                Salta e vedi ordine
              </button>
            </div>
          </section>
        );
      })()}
    </div>
  );
}

function currentSlotLabel(slot) {
  if (slot === "day") return "Giorno (08–18)";
  if (slot === "evening") return "Sera (18–21) +20%";
  if (slot === "night") return "Mattino presto (06:30–08) +50%";
  return "Seleziona un orario";
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-dapnow-muted font-semibold">
        {label}{required ? <span className="text-dapnow-red ml-0.5">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Header({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Icon className="h-5 w-5" /></span>
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
        <p className="text-sm text-dapnow-muted">{desc}</p>
      </div>
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className={`rounded-2xl ${muted ? "bg-dapnow-bg" : "bg-dapnow-bg"} p-4`}>
      <p className="overline">{label}</p>
      <p className="font-display text-xl font-bold tracking-tight mt-1">{value}</p>
    </div>
  );
}

// Phase 43.32 — Notice contestuale: cosa manca per poter cliccare "Continua"
function Step1MissingNotice({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid="missing-fields-notice"
      className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-3"
    >
      <span className="size-5 mt-0.5 shrink-0 text-amber-600">
        <svg viewBox="0 0 24 24" fill="currentColor" className="size-5">
          <path d="M12 2L1 21h22L12 2zm0 7.5l7 12H5l7-12zm-1 4v3h2v-3h-2zm0 4v2h2v-2h-2z"/>
        </svg>
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold">Per continuare, completa:</p>
        <ul className="mt-1 list-disc list-inside space-y-0.5">
          {items.map((it, i) => (
            <li key={i} data-testid={`missing-field-${i}`}>{it}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Row({ k, v, bold, accent, muted }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-dapnow-muted text-xs italic" : "text-dapnow-muted"}>{k}</span>
      <span className={`${bold ? "font-display font-bold" : "font-medium"} ${accent ? "text-dapnow-red" : ""}`}>{v}</span>
    </div>
  );
}
